import React, { FC, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { logoutUser } from '../reducers/userReducer';
import { notify } from '../reducers/notificationReducer';
import DesktopUserMenu from './DesktopUserMenu';
import SearchBar from './SearchBar';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AppBar, Toolbar, Button, useMediaQuery, IconButton, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SearchIcon from '@mui/icons-material/Search';
import Logo from './Logo';
import WebSideNav from './WebSideNav';
import MobileUserMenu from './Mobile/MobileUserMenu';
import { Capacitor } from '@capacitor/core';
import type { UserState } from '../types/common';

const NavBar: FC = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const user = useAppSelector((state) => state.user) as UserState | null;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || Capacitor.getPlatform() !== 'web';

  const handleLogout = () => {
    if (!user) return;
    dispatch(logoutUser());
    dispatch(notify(`u/${user.username} logged out`, 'success'));
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        backgroundColor: theme.tokens.colors.glassBackground,
        backdropFilter: theme.tokens.effects.glassBlur,
        WebkitBackdropFilter: theme.tokens.effects.glassBlur,
        borderBottom: 'var(--border-thin-solid)',
        borderColor: theme.tokens.colors.glassBorder,
        boxShadow: `inset 0 1px 0 ${theme.tokens.colors.glassSpec}`,
      }}
    >
      <Toolbar disableGutters={isMobile}>
        {!searchOpen && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                [theme.breakpoints.down('sm')]: { marginLeft: 'var(--space-stack-normal)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 'fit-content' }}>
                <WebSideNav mode="menu" />
                <Button
                  sx={{
                    fontFamily: theme.tokens.typography.brandFontFamily,
                    textTransform: 'var(--space-none)',
                    fontSize: 'brand',
                    padding: 'var(--space-none)',
                    width: '7em',
                    '& .MuiButton-startIcon': {
                      width: '100%',
                      height: '100%',
                      '& .MuiBox-root': {
                        width: '100%',
                        height: '100%',
                        margin: 0,
                        display: 'flex',
                        '& svg': { width: 'auto', height: 'auto' },
                      },
                    },
                  }}
                  color="primary"
                  component={RouterLink}
                  to="/"
                  startIcon={<Logo />}
                  size="large"
                ></Button>
              </Box>

              {!isMobile && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    maxWidth: '600px',
                    margin: '0 auto',
                  }}
                >
                  <SearchBar isMobile={false} setSearchOpen={() => {}} />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
              <Button
                color="primary"
                onClick={() => navigate('/submit')}
                sx={{ textTransform: 'none' }}
                startIcon={<PostAddIcon />}
                size="large"
              >
                Create Post
              </Button>
              {isMobile ? (
                <>
                  <IconButton
                    color="primary"
                    onClick={() => setSearchOpen((prevState) => !prevState)}
                    size="large"
                    sx={{ padding: 'var(--space-inline-compact)' }}
                  >
                    <SearchIcon />
                  </IconButton>
                  <MobileUserMenu user={user} handleLogout={handleLogout} />
                </>
              ) : (
                <DesktopUserMenu user={user} handleLogout={handleLogout} />
              )}
            </Box>
          </>
        )}
        {searchOpen && isMobile && <SearchBar isMobile={true} setSearchOpen={setSearchOpen} />}
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
